import { alertClasses, createTheme } from "@mui/material"
import type {} from "@mui/x-date-pickers/themeAugmentation"

import colors from "@/styles/colors"

const rootElement = document.querySelector(".root")!

const theme = createTheme({
  components: {
    MuiAccordion: {
      styleOverrides: {
        root: {
          width: "100%",
          margin: 0,
          border: `1px solid ${colors.grey["300"]}`,
          borderRadius: "0.375rem",
          "&.Mui-expanded": { margin: 0 },
          "&::before": { display: "none" },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: "0 1.5rem 1.5rem",
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: "1.5rem",
          "&.MuiAccordionSummary-root, .MuiAccordionSummary-content": {
            minHeight: "fit-content",
          },
          "& .MuiAccordionSummary-expandIconWrapper": {
            color: colors.grey["600"],
          },
          "& .MuiAccordionSummary-content, .MuiAccordionSummary-content.Mui-expanded": {
            margin: 0,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiAutocomplete-inputRoot": {
            gap: "0.5rem",
            paddingTop: "0.5rem",
            paddingBottom: "0.5rem",
            paddingLeft: "1rem",
            "& .MuiAutocomplete-input": {
              minWidth: "auto",
              width: "100%",
              padding: "0",
            },
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          padding: "0.75rem 1.5rem",
          fontWeight: 400,
          display: "flex",
          "& .MuiAlert-icon, & .MuiAlert-message": {
            paddingTop: "0.375rem",
            paddingBottom: "0.375rem",
          },
          "& .MuiAlert-icon .MuiSvgIcon-root": {
            width: "24px",
            height: "24px",
          },
          "& .MuiAlertTitle-root": {
            fontSize: "1rem",
            lineHeight: "1.75rem",
            fontWeight: 400,
            margin: 0,
          },
          "& .MuiAlert-message": {
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "center",
            rowGap: "0.25rem",
            paddingTop: "0.25rem",
            paddingBottom: "0.25rem",
            marginRight: "auto",
          },
          "& .MuiAlert-action": {
            margin: 0,
            padding: 0,
            marginLeft: "1rem",
            alignSelf: "center",
          },
          [`&.${alertClasses.filled}.${alertClasses.colorInfo}`]: {
            backgroundColor: colors.primary["100"],
            color: colors.primary.main,
          },
          [`&.${alertClasses.filled}.${alertClasses.colorSuccess}`]: {
            backgroundColor: colors.tertiary.green["100"],
            color: colors.tertiary.green["900"],
          },
          [`&.${alertClasses.filled}.${alertClasses.colorWarning}`]: {
            backgroundColor: colors.tertiary.yellow["100"],
            color: colors.tertiary.yellow["900"],
          },
          [`&.${alertClasses.filled}.${alertClasses.colorError}`]: {
            backgroundColor: colors.secondary["100"],
            color: colors.secondary["800"],
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          "& .PrivateSwitchBase-input": {
            width: "100%",
            height: "100%",
          },
        },
      },
    },
    MuiFormGroup: {
      styleOverrides: {
        root: {
          rowGap: "0",
          columnGap: "1rem",
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          "& .MuiInputLabel-root": {
            position: "static",
            maxWidth: "100%",
            marginBottom: "8px",
            fontSize: "0.75rem",
            lineHeight: "0.875rem",
            transform: "none",
            color: `${colors.grey["800"]} !important`,
          },
          "& .MuiInputLabel-asterisk": {
            color: colors.secondary["600"],
          },
          "& .MuiInputBase-root, & .MuiInputBase-input": {
            backgroundColor: "#FFF",
            "&.Mui-disabled": {
              backgroundColor: colors.grey["200"],
            },
          },
          "& .MuiInputBase-root": {
            "&.MuiInputBase-multiline": {
              padding: "0",
            },
            "&.Mui-focused:not(.Mui-disabled) fieldset": {
              zIndex: 1,
              border: "1px solid #B2C7EB",
              boxShadow: "0 0 0 4px #BFDBFE",
            },
            "&.Mui-error:not(.Mui-disabled) fieldset": {
              border: `1px solid ${colors.secondary["300"]} !important`,
            },
            "&.Mui-focused.Mui-error:not(.Mui-disabled) fieldset": {
              boxShadow: `0 0 0 4px ${colors.secondary["200"]}`,
            },
            "&.Mui-disabled fieldset": {
              borderColor: colors.grey["300"],
            },
            // Overrides for DatePicker placeholder display.
            "& .MuiInputBase-input.MuiOutlinedInput-input::-webkit-input-placeholder": {
              opacity: "1 !important",
            },
          },
          "& .MuiInputBase-input": {
            height: "1.5rem",
            padding: "0.5rem 1rem",
            fontSize: "0.875rem",
            lineHeight: "1.5rem",
            textOverflow: "ellipsis",
            "&::placeholder": {
              opacity: 1,
              WebkitTextFillColor: colors.grey["500"],
              color: colors.grey["500"],
            },
            "&:focus": {
              boxShadow: "none",
            },
            "&.Mui-disabled": {
              WebkitTextFillColor: "#9CA3AF",
              color: "#9CA3AF",
            },
          },
          "& fieldset": {
            top: "0",
            border: `1px solid ${colors.grey["400"]}`,
            "& legend": {
              display: "none",
            },
          },
          "& .MuiFormHelperText-root": {
            color: colors.grey["600"],
            marginLeft: "0",
            marginRight: "0",
            fontSize: "0.75rem",
            "&.Mui-error": {
              color: colors.secondary["600"],
            },
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          margin: 0,
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          margin: "0",
        },
      },
    },
    MuiPopover: {
      defaultProps: {
        container: rootElement,
      },
    },
    MuiPopper: {
      defaultProps: {
        container: rootElement,
      },
    },
    MuiDialog: {
      defaultProps: {
        container: rootElement,
      },
    },
    MuiModal: {
      defaultProps: {
        container: rootElement,
      },
    },
    MuiChip: {
      styleOverrides: {
        colorPrimary: {
          background: colors.grey["300"],
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.grey["400"],
          },
          "&.Mui-focused, &&:hover": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.primary["200"],
            },
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: "0.5rem",
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          "& .MuiSelect-select.MuiInputBase-input": {
            minHeight: "1.5rem",
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          "& .MuiSwitch-switchBase.Mui-checked": {
            color: colors.primary["400"],
            "& .MuiSwitch-track": {
              backgroundColor: colors.primary["400"],
            },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          padding: "0.5rem 1.5rem",
          textTransform: "none",
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          boxShadow: `inset 0 -1px 0 0 ${colors.grey["300"]}`,
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          color: colors.grey["700"],
          borderColor: colors.grey["300"],
          borderCollapse: "separate",
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          borderTopLeftRadius: "0.375rem",
          borderTopRightRadius: "0.375rem",
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          borderBottomLeftRadius: "0.375rem",
          borderBottomRightRadius: "0.375rem",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          backgroundColor: "#FFF",
          "&:last-child .MuiTableCell-body": {
            "&:first-of-type": {
              borderBottomLeftRadius: "0.25rem",
            },
            "&:last-of-type": {
              borderBottomRightRadius: "0.25rem",
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: colors.grey["300"],
          color: colors.grey["700"],
          fontSize: "0.875rem",
          lineHeight: "1.125rem",
          "&.MuiTableCell-head": {
            padding: "1rem",
            letterSpacing: "0.013rem",
            backgroundColor: colors.grey["100"],
            "&:first-of-type": {
              borderTopLeftRadius: "0.25rem",
            },
            "&:last-of-type": {
              borderTopRightRadius: "0.25rem",
            },
          },
          "&.MuiTableCell-body": {
            padding: "0.75rem 1rem",
            letterSpacing: "0.011rem",
            "&:first-of-type": {
              borderLeftWidth: "1px",
            },
            "&:last-of-type": {
              borderRightWidth: "1px",
            },
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: "0.25rem",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          "&.date-tooltip": {
            backgroundColor: colors.grey["700"],
            color: "white",
            fontSize: "10px",
            fontFamily: "Roboto",
            fontWeight: 500,
            padding: "4px 8px",
            borderRadius: "4px",
            maxWidth: "none",
          },
        },
        arrow: {
          "&.date-tooltip": {
            color: colors.grey["700"],
          },
        },
      },
    },
  },
  typography: {
    fontFamily: "Inter, sans-serif",
  },
  palette: {
    ...colors,
  },
})

export default theme
